import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Monitor, ExternalLink, CheckCircle2, Apple } from 'lucide-react';
import { isAppLaunch } from '../utils/launchContext';
import { IS_INTERNAL_TESTING_STAGE, buildPlayStoreDeferredUrl } from '../utils/appStoreLinks';
import { storePendingInviteId } from '../utils/deepLinkHandler';
import { useState } from 'react';

/**
 * InviteAppGate
 * ------------------------------------------------------------------
 * Wraps an invite landing page with a smart "do you have the app?" gate.
 *
 *  • Inside the PWA / TWA → renders `children` as-is (existing claim flow).
 *  • iOS Safari (logged in) → renders `children` directly (PWA in Safari).
 *  • iOS Safari (not logged in) → shows "register to accept" screen, stores
 *    invite ID so post-registration auto-redirects to the invite.
 *  • Android browser → renders the "Get the app" CTA with Play Store button.
 *  • Desktop browser → renders a QR code for scanning.
 */
export default function InviteAppGate({
  children,
  invitedToLabel = "You've been invited to join Allure",
  invitedSubLabel = '',
  inviteId,
  reTapUrl,
  storePending,
}) {
  const navigate = useNavigate();
  const inApp = useMemo(() => isAppLaunch(), []);
  const isDesktop = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return !/Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  }, []);
  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
  }, []);

  // Always render children inside the installed app
  if (inApp) return <>{children}</>;

  // iOS users can run Allure in Safari — no App Store install needed yet.
  // If they're already logged in, render the invite page directly.
  // If not, store the invite ID so post-registration auto-redirects them.
  if (isIOS) {
    const hasToken = Boolean(
      typeof localStorage !== 'undefined' && localStorage.getItem('token')
    );
    if (hasToken) return <>{children}</>;
    // Store invite so registration/login flow resumes the invite afterwards.
    // Host/staff invite pages pass their own `storePending` — the default
    // guest key would otherwise route them to the wrong invite type.
    if (inviteId) {
      if (typeof storePending === 'function') storePending(inviteId);
      else storePendingInviteId(inviteId);
    }
  }

  const fullInviteUrl = reTapUrl || (typeof window !== 'undefined' ? window.location.href : '');
  // Deferred deep link: carry the current invite path through the Play Store
  // install so the app opens straight onto this invite on first launch.
  const deepLinkPath = typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search}`
    : '';
  const playStoreUrl = buildPlayStoreDeferredUrl(deepLinkPath);

  return (
    <div
      data-testid="invite-app-gate"
      style={{
        minHeight: '100dvh',
        backgroundColor: 'var(--app-bg)',
        padding: '0 22px 40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 22,
      }}
    >
      <img
        src="/allure-logo.png"
        alt="Allure"
        style={{ width: 132, height: 118, objectFit: 'contain', marginTop: 56 }}
      />

      <div style={{ textAlign: 'center', maxWidth: 460 }}>
        <h1
          data-testid="invite-app-gate-heading"
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 30,
            fontWeight: 700,
            color: 'var(--text)',
            lineHeight: 1.1,
            margin: 0,
          }}
        >
          {invitedToLabel}
        </h1>
        {invitedSubLabel ? (
          <p
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 19,
              color: 'var(--text-2)',
              marginTop: 8,
              marginBottom: 0,
              lineHeight: 1.3,
            }}
          >
            {invitedSubLabel}
          </p>
        ) : null}
      </div>

      {isIOS ? (
        <IOSGate navigate={navigate} />
      ) : isDesktop ? (
        <DesktopGate inviteUrl={fullInviteUrl} playStoreUrl={playStoreUrl} />
      ) : (
        <MobileGate inviteUrl={fullInviteUrl} playStoreUrl={playStoreUrl} />
      )}

      {IS_INTERNAL_TESTING_STAGE && !isIOS ? (
        <div
          data-testid="invite-app-gate-internal-testing-note"
          style={{
            marginTop: 18,
            padding: '10px 14px',
            border: '1px dashed var(--card-border)',
            borderRadius: 12,
            fontFamily: "'Inria Serif', serif",
            fontSize: 14,
            color: 'var(--text-3)',
            lineHeight: 1.35,
            textAlign: 'center',
            maxWidth: 460,
          }}
        >
          Internal testing build — the Play Store button opens the private
          testers&nbsp;page. Once Allure is publicly listed this banner will
          disappear automatically.
        </div>
      ) : null}
    </div>
  );
}

function IOSGate({ navigate }) {
  return (
    <div
      data-testid="invite-app-gate-ios"
      style={{
        width: '100%',
        maxWidth: 460,
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        boxShadow: 'var(--premium-shadow-main)',
        borderRadius: 18,
        padding: '22px 20px',
        display: 'grid',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Apple size={22} color="var(--text-2)" strokeWidth={1.8} />
        <h2
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 22,
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Accept your invite in Safari
        </h2>
      </div>
      <p
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 17,
          color: 'var(--text-2)',
          margin: 0,
          lineHeight: 1.35,
        }}
      >
        Allure runs right in Safari — no download needed for iPhone users.
        Create an account or log in to claim your invite.
      </p>

      <button
        data-testid="invite-app-gate-ios-register"
        onClick={() => navigate('/register')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '14px 18px',
          borderRadius: 9999,
          background: 'var(--accent-purple)',
          color: '#FFFFFF',
          border: 'none',
          fontFamily: "'Inria Serif', serif",
          fontSize: 20,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 10px 24px rgba(196, 18, 48, 0.22)',
        }}
      >
        Create account
      </button>

      <button
        data-testid="invite-app-gate-ios-login"
        onClick={() => navigate('/login')}
        style={{
          border: '1px solid var(--card-border)',
          background: 'var(--surface)',
          color: 'var(--text)',
          borderRadius: 9999,
          padding: '12px 18px',
          fontFamily: "'Inria Serif', serif",
          fontSize: 17,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Already have an account? Log in
      </button>
    </div>
  );
}

function MobileGate({ inviteUrl, playStoreUrl }) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_err) {
      /* clipboard may be unavailable on older mobile browsers */
    }
  };

  return (
    <>
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          background: 'var(--surface)',
          border: '1px solid var(--card-border)',
          boxShadow: 'var(--premium-shadow-main)',
          borderRadius: 18,
          padding: '22px 20px',
          display: 'grid',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Smartphone size={22} color="#04A628" strokeWidth={1.8} />
          <h2
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 22,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Get the Allure app first
          </h2>
        </div>
        <p
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 17,
            color: 'var(--text-2)',
            margin: 0,
            lineHeight: 1.35,
          }}
        >
          To accept this invite you&apos;ll need the Allure app on your phone.
          Install it from Google Play — your invite travels with the install
          and opens automatically the first time you launch the app.
        </p>

        <a
          data-testid="invite-app-gate-play-store-button"
          href={playStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '14px 18px',
            borderRadius: 9999,
            background: '#04A628',
            color: '#FFFFFF',
            textDecoration: 'none',
            fontFamily: "'Inria Serif', serif",
            fontSize: 22,
            fontWeight: 700,
            boxShadow: '0 10px 24px rgba(4, 166, 40, 0.22)',
          }}
        >
          Get the app
          <ExternalLink size={18} strokeWidth={2} />
        </a>

        <button
          data-testid="invite-app-gate-copy-link-button"
          onClick={copyLink}
          style={{
            border: '1px solid var(--card-border)',
            background: 'var(--surface)',
            color: 'var(--text)',
            borderRadius: 9999,
            padding: '12px 18px',
            fontFamily: "'Inria Serif', serif",
            fontSize: 17,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          {copied ? (
            <>
              <CheckCircle2 size={18} color="#04A628" strokeWidth={2} /> Link copied
            </>
          ) : (
            'Copy the invite link'
          )}
        </button>
      </div>

      <ol
        style={{
          maxWidth: 460,
          padding: '0 4px 0 20px',
          margin: 0,
          fontFamily: "'Inria Serif', serif",
          fontSize: 15,
          color: 'var(--text-2)',
          lineHeight: 1.45,
          textAlign: 'left',
        }}
      >
        <li>Tap &quot;Get the app&quot; and install Allure from Google Play.</li>
        <li>Open Allure — your invite will be waiting on the first screen.</li>
        <li>If it doesn&apos;t appear, come back here and tap this invite link again (or paste the copied link inside the app).</li>
      </ol>
    </>
  );
}

function DesktopGate({ inviteUrl, playStoreUrl }) {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 460,
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        boxShadow: 'var(--premium-shadow-main)',
        borderRadius: 18,
        padding: '22px 20px',
        display: 'grid',
        gap: 14,
        justifyItems: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Monitor size={22} color="#04A628" strokeWidth={1.8} />
        <h2
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 22,
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Open this on your phone
        </h2>
      </div>
      <p
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 17,
          color: 'var(--text-2)',
          margin: 0,
          lineHeight: 1.35,
          textAlign: 'center',
        }}
      >
        Allure lives on mobile. Scan this QR code with your phone&apos;s
        camera to continue your invite there.
      </p>
      <div
        style={{
          padding: 14,
          borderRadius: 14,
          background: 'var(--surface-2)',
          border: '1px solid var(--card-border)',
        }}
        data-testid="invite-app-gate-desktop-qr"
      >
        <QRCodeSVG value={inviteUrl} size={208} level="M" includeMargin={false} fgColor="var(--text)" bgColor="transparent" />
      </div>
      <a
        data-testid="invite-app-gate-play-store-button-desktop"
        href={playStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--status-success-text)',
          fontFamily: "'Inria Serif', serif",
          fontSize: 15,
          textDecoration: 'underline',
        }}
      >
        Or open the Play Store listing in a new tab
      </a>
    </div>
  );
}
